/**
 * Presentational Patient Notification worklist and local-only controls.
 */

import { useEffect, useMemo, useState } from "react";
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import type { SortDirection } from "../../components/custom-table/types";
import { clinicalTrialsService } from "../../api/clinicalTrialsService";
import { expandRowsForTab } from "../../api/transformers";
import { useOpenNoteMrns } from "../../hooks/useOpenNoteMrns";
import { mockPatients } from "../../data/mockPatients";
import type { DocumentType, Patient, UnreadDocumentCounts } from "../../types";
import type {
  NotificationFacetOptions,
  NotificationFilters,
} from "../../utils/notificationFilters";
import {
  mergeStoredPatientTags,
  writeStoredPatientTags,
} from "../../utils/patientTagStorage";
import { DateRangeSelector } from "./components/DateRangeSelector";
import { DocumentTypeTabs } from "./components/DocumentTypeTabs";
import { FilterBadges } from "./components/FilterBadges";
import { FilterControls } from "./components/FilterControls";
import { PatientTable } from "./components/PatientTable";
import "./PatientNotificationView.scss";

/** Controlled data and callbacks for the notification worklist. */
interface PatientNotificationViewProps {
  onPatientSelect: (patient: Patient, documentType: DocumentType) => void;
  onMarkUnread?: (patient: Patient, documentType: DocumentType) => void;
  onMarkRead?: (patient: Patient, documentType: DocumentType) => void;
  onOpenNotes?: (patient: Patient, documentType: DocumentType) => void;
  activeDocumentType: DocumentType;
  onDocumentTypeChange: (type: DocumentType) => void;
  onViewChange: (view: string) => void;
  patients?: Patient[];
  unreadDocumentCounts?: UnreadDocumentCounts;
  selectedDate?: string;
  onDateChange?: (date: string) => void;
  isLoading?: boolean;
  filters: NotificationFilters;
  onFiltersChange: (filters: NotificationFilters) => void;
  onResetFilters: () => void;
  facetOptions: NotificationFacetOptions;
  appointmentBounds?: { min?: string; max?: string };
  areFacetsLoading?: boolean;
  facetError?: string | null;
  onRetryFacets?: () => void;
  /** Controlled free-text search (server-backed). */
  searchQuery: string;
  /** Search-input change handler. */
  onSearchChange: (query: string) => void;
  /** Total matching patients from the server (pre-document expansion). */
  totalCount: number;
  /** 0-indexed page currently shown. */
  page: number;
  /** Total pages for the current filters (from the API). */
  totalPages: number;
  /** Document rows matching the filters across every page (from the API). */
  totalDocumentCount: number;
  /** Document rows carried by the pages before this one (from the API). */
  documentOffset: number;
  /** Request another page of results. */
  onPageChange: (page: number) => void;
  sortKey?: string | null;
  sortDirection?: SortDirection;
  onSortChange?: (sortKey: string, direction: SortDirection) => void;
  /** Bump facet reload after a successful same-session tag edit. */
  onTagsUpdated?: () => void;
}

/**
 * Render the notification controls and one server page of rows.
 *
 * PRD facets, free-text search, sort, and pagination are controlled by the
 * data-owning page. Assignment-initial tags remain browser-local. The Notes
 * action is navigation-only and does not combine collaboration state with
 * document badges.
 *
 * @param props - Notification rows, filters, metadata, and callbacks
 * @returns Patient Notification worklist
 */
export function PatientNotificationView({
  onPatientSelect,
  onMarkUnread,
  onMarkRead,
  onOpenNotes,
  activeDocumentType,
  onDocumentTypeChange,
  onViewChange,
  patients: patientsProp,
  unreadDocumentCounts = {},
  selectedDate: selectedDateProp,
  onDateChange,
  isLoading,
  filters,
  onFiltersChange,
  onResetFilters,
  facetOptions,
  appointmentBounds,
  areFacetsLoading,
  facetError,
  onRetryFacets,
  searchQuery,
  onSearchChange,
  totalCount,
  totalDocumentCount,
  documentOffset,
  page,
  totalPages,
  onPageChange,
  sortKey,
  sortDirection,
  onSortChange,
  onTagsUpdated,
}: PatientNotificationViewProps) {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const [selectedDateLocal, setSelectedDateLocal] = useState(
    yesterday.toISOString().split("T")[0],
  );
  const [patients, setPatients] = useState<Patient[]>(() =>
    mergeStoredPatientTags(patientsProp ?? mockPatients),
  );
  const selectedDate = selectedDateProp ?? selectedDateLocal;
  const setSelectedDate = onDateChange ?? setSelectedDateLocal;
  // Notes are not carried on worklist rows, so cross-reference the facility
  // open-notes inbox to light up the notes action for patients that have one.
  const mrnsWithNotes = useOpenNoteMrns();

  useEffect(() => {
    if (patientsProp) setPatients(mergeStoredPatientTags(patientsProp));
  }, [patientsProp]);

  /**
   * Persist shared CRC tags and retain browser-local assignment initials.
   *
   * @param updatedPatient - Patient containing replacement tag values
   * @returns Nothing
   */
  const handlePatientUpdate = (updatedPatient: Patient) => {
    // Tags are per-PATIENT. The tag cell renders on EXPANDED rows whose id is `${mrn}__${docId}`,
    // so match/update by MRN (not by row id) — otherwise the base patient is never found and the
    // save never fires. Update only patientTags so the base row keeps its other fields.
    const nextTags = updatedPatient.patientTags ?? [];
    setPatients((previous) => {
      const current = previous.find(
        (patient) => patient.mrn === updatedPatient.mrn,
      );
      if (
        current &&
        JSON.stringify(current.patientTags ?? []) !== JSON.stringify(nextTags)
      ) {
        clinicalTrialsService
          .updatePatientTags(updatedPatient.mrn, nextTags)
          .then(() => {
            onTagsUpdated?.();
          })
          .catch((error: unknown) => {
            const message =
              error instanceof Error
                ? error.message
                : "Failed to save patient tags";
            toast.error(message, "Patient tags not saved");
          });
      }
      return previous.map((patient) =>
        patient.mrn === updatedPatient.mrn
          ? { ...patient, patientTags: nextTags }
          : patient,
      );
    });
    writeStoredPatientTags(updatedPatient.mrn, nextTags);
  };

  // Diagnosis description -> ICD code(s), so the Diagnosis filter type-ahead matches by
  // code too (the facet option VALUE stays the description the backend filters on).
  // Best-effort from the current page; full option lists still come from /facets.
  const diagnosisCodeIndex = useMemo(() => {
    const index: Record<string, string[]> = {};
    const add = (description?: string, code?: string) => {
      if (!description || !code) return;
      const codes = index[description] ?? (index[description] = []);
      if (!codes.includes(code)) codes.push(code);
    };
    patients.forEach((patient) => {
      add(patient.diagnosis, patient.dxCode);
      patient.conditionCancers?.forEach((cancer) =>
        add(cancer.description, cancer.code),
      );
    });
    return index;
  }, [patients]);

  const tablePatients = expandRowsForTab(patients, activeDocumentType);
  // Record range over document rows, not patients: pages are cut by patient, so a
  // page's first record number depends on how many documents the earlier pages
  // held — only the server can supply it. Once supplied the range renders in one
  // shape on every page, single-page days included, so the footer never changes
  // format under the user. A zero total means an API deployed before these fields
  // existed, since a populated table always has records: the offset defaults to 0
  // there and would misreport every page but the first as `1-50 of 50`, so omit
  // the range rather than state it wrongly. It returns once the API carries them.
  const hasServerRecordRange = totalDocumentCount > 0;
  const rangeStart = documentOffset + 1;
  const rangeEnd = documentOffset + tablePatients.length;
  const canGoPrevious = page > 0;
  const canGoNext = totalPages > 0 && page + 1 < totalPages;

  return (
    <div className="patient-notification-view">
      <div className="container-app py-6 patient-notification-view__content">
        <div className="patient-notification-view__controls">
          <header className="mb-6 flex items-start justify-between">
            <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
              Patient Notification View
            </h1>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => onViewChange("notes")}
                className="inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium outline-none transition-colors focus:ring-2"
                style={{
                  backgroundColor: "var(--color-input-bg)",
                  borderColor: "var(--color-input-border)",
                  color: "var(--color-text-primary)",
                }}
              >
                Notes
              </button>
              <DateRangeSelector
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
              />
            </div>
          </header>
          <div className="mb-6">
            <DocumentTypeTabs
              activeType={activeDocumentType}
              onTypeChange={onDocumentTypeChange}
              unreadCounts={unreadDocumentCounts}
            />
          </div>
          <div className="mb-6 flex items-center gap-3">
            <input
              type="search"
              aria-label="Search patients"
              placeholder="Search by patient name, MRN, diagnosis, status, or tag..."
              value={searchQuery}
              onChange={(event) => onSearchChange(event.target.value)}
              className="w-full max-w-2xl rounded-md border border-[var(--color-input-border)] bg-[var(--color-input-bg)] px-4 py-3"
            />
            {onFiltersChange && (
              <FilterControls
                activeFilters={filters ?? {}}
                onApply={onFiltersChange}
                options={facetOptions ?? {}}
                appointmentBounds={appointmentBounds}
                isLoading={areFacetsLoading}
                error={facetError}
                onRetry={onRetryFacets}
                optionAliases={{ diagnosis: diagnosisCodeIndex }}
              />
            )}
          </div>
          {onFiltersChange && onResetFilters && (
            <FilterBadges
              filters={filters ?? {}}
              onChange={onFiltersChange}
              onReset={onResetFilters}
            />
          )}
        </div>
        <div
          className="patient-notification-view__table-scroll"
          data-notification-scroll-container
        >
          <PatientTable
            patients={tablePatients}
            documentType={activeDocumentType}
            onPatientSelect={onPatientSelect}
            onPatientUpdate={handlePatientUpdate}
            onMarkUnread={onMarkUnread}
            onMarkRead={onMarkRead}
            onOpenNotes={onOpenNotes}
            mrnsWithNotes={mrnsWithNotes}
            isLoading={isLoading}
            sortKey={sortKey}
            sortDirection={sortDirection}
            onSortChange={onSortChange}
          />
        </div>
        <div className="patient-notification-view__results flex flex-wrap items-center justify-between gap-3 text-sm text-[var(--color-text-secondary)]">
          <span>
            {isLoading
              ? "Loading records…"
              : totalCount === 0
                ? "No matching records"
                : hasServerRecordRange
                  ? `${rangeStart}–${rangeEnd} of ${totalDocumentCount} records`
                  : `${tablePatients.length} records`}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Previous page"
              disabled={!canGoPrevious || isLoading}
              onClick={() => onPageChange(page - 1)}
              className="rounded-md border px-3 py-1.5 text-sm font-medium disabled:opacity-50"
              style={{
                backgroundColor: "var(--color-input-bg)",
                borderColor: "var(--color-input-border)",
                color: "var(--color-text-primary)",
              }}
            >
              Previous
            </button>
            <span aria-live="polite">
              Page {totalPages === 0 ? 0 : page + 1} of {totalPages}
            </span>
            <button
              type="button"
              aria-label="Next page"
              disabled={!canGoNext || isLoading}
              onClick={() => onPageChange(page + 1)}
              className="rounded-md border px-3 py-1.5 text-sm font-medium disabled:opacity-50"
              style={{
                backgroundColor: "var(--color-input-bg)",
                borderColor: "var(--color-input-border)",
                color: "var(--color-text-primary)",
              }}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
